import React, { useState, useRef, useEffect } from 'react';
import { FEATURES, UI_CONSTANTS } from '../../utils/constants';
import { useSpeechRecognition } from '../../hooks/useSpeech';
import { createNewMessage } from '../../utils/messageHandlers';
import { getFilteredMessages, getRandomDefaultPhoto } from '../../utils/profileUtils';
import MessageCard from './MessageCard';
import Timeline from './Timeline';
import StudentProfile from './StudentProfile';
import ChatInput from './ChatInput';
import ErrorDisplay from './ErrorDisplay';
import CategoryFilters from './CategoryFilters';
import DemoControls from './DemoControls';

const ObservationChatWindow = () => {
  // Feature flags
  const [features, setFeatures] = useState({
    [FEATURES.SPEECH_RECOGNITION]: true,
    [FEATURES.TIMELINE]: true,
    [FEATURES.STUDENT_PROFILES]: true,
    [FEATURES.CATEGORY_FILTERS]: true
  });

  // Core state
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [activeStudent, setActiveStudent] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [knownNames, setKnownNames] = useState(new Set());
  const [studentImages, setStudentImages] = useState({});
  const [highlightedMessage, setHighlightedMessage] = useState(null);

  // Refs
  const messagesEndRef = useRef(null);

  const handleSendMessage = () => {
    try {
      if (inputText.trim() === '') return;

      const newMessage = createNewMessage(inputText);
      console.debug('New message object:', newMessage);

      setMessages(prev => [...prev, newMessage]);
      
      // Assign random photos to new names
      const newImages = {};
      newMessage.names.forEach(name => {
        if (!knownNames.has(name) && !studentImages[name]) {
          newImages[name] = getRandomDefaultPhoto();
          console.log(`Assigned photo for ${name}:`, newImages[name]); // Debug log
        }
      });
      
      setStudentImages(prev => {
        const updated = {...prev, ...newImages};
        console.log('Updated student images:', updated); // Debug log
        return updated;
      });
      setKnownNames(prev => new Set([...prev, ...newMessage.names]));
      setInputText('');

      if (!activeStudent && newMessage.names.length > 0) {
        setActiveStudent(newMessage.names[0]);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
    }
  };

  const {
    isListening,
    setIsListening,
    speechRecognition,
    error,
    setError
  } = useSpeechRecognition(features, setFeatures, setInputText, handleSendMessage);

  // Auto-scroll messages - this appears to both scroll when new messages are added and when messages are clicked
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, setError]);

  // Event handlers
  const handleStartListening = () => {
    try {
      if (speechRecognition.current) {
        speechRecognition.current.start();
        setIsListening(true);
        setError(null);
      }
    } catch (err) {
      console.error('Failed to start speech recognition:', err);
      setError('Failed to start speech recognition. Please try again.');
      setIsListening(false);
    }
  };

  const handleStopListening = () => {
    if (speechRecognition.current) {
      speechRecognition.current.stop();
      setIsListening(false);
    }
  };

  const handleImageUpload = async (studentName, file) => {
    try {
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file.');
        return;
      }

      const fakeUploadUrl = URL.createObjectURL(file);
      setStudentImages(prev => ({
        ...prev,
        [studentName]: fakeUploadUrl
      }));
    } catch (err) {
      console.error('Image upload failed:', err);
      setError('Failed to upload image. Please try again.');
    }
  };

  const handleTimelineClick = (messageId) => {
    try {
      setHighlightedMessage(messageId);
      const element = document.getElementById(`message-${messageId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => setHighlightedMessage(null), UI_CONSTANTS.HIGHLIGHT_DURATION);
      }
    } catch (err) {
      console.error('Timeline navigation failed:', err);
      setError('Failed to navigate to message.');
    }
  };

  const handleToggleSentiment = (messageId, newSentiment) => {
    setMessages(prevMessages =>
      prevMessages.map(msg =>
        msg.id === messageId ? { ...msg, sentiment: newSentiment } : msg
      )
    );
  };

  const handleUpdateCategory = (messageId, newCategory) => {
    setMessages(prevMessages =>
      prevMessages.map(msg =>
        msg.id === messageId ? { ...msg, category: newCategory } : msg
      )
    );
  };

  const handleAddNote = (messageId, noteText) => {
    setMessages(prevMessages =>
      prevMessages.map(msg =>
        msg.id === messageId ? { ...msg, note: noteText } : msg
      )
    );
  };

  const handleAddAttachment = (messageId, file) => {
    setMessages(prevMessages =>
      prevMessages.map(msg =>
        msg.id === messageId ? { ...msg, attachment: file } : msg
      )
    );
  };

  return (
    <div className="flex flex-col max-w-6xl mx-auto p-4">
      {/* Error Display */}
      <ErrorDisplay error={error} />

      <div className="flex gap-4">
        {/* Message Log */}
        <div className="w-1/2 bg-white shadow-md rounded-lg overflow-hidden">
          <div className="p-4 bg-gray-100 font-bold text-center">
            Message Log
          </div>
          
          <div className="h-[calc(100vh-300px)] overflow-y-auto p-4">
            {messages.map((msg, index) => (
              <MessageCard
                key={msg.id}
                message={msg}
                index={index}
                isHighlighted={highlightedMessage === msg.id}
                onToggleSentiment={handleToggleSentiment}
                onUpdateCategory={handleUpdateCategory}
                onAddNote={handleAddNote}
                onAddAttachment={handleAddAttachment}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>

          <ChatInput
            inputText={inputText}
            setInputText={setInputText}
            onSend={handleSendMessage}
            isListening={isListening}
            onStartListening={handleStartListening}
            onStopListening={handleStopListening}
            speechEnabled={features[FEATURES.SPEECH_RECOGNITION]}
          />
        </div>

        {/* Student Profiles */}
        {features[FEATURES.STUDENT_PROFILES] && (
          <div className="w-1/2 bg-white shadow-md rounded-lg overflow-hidden flex flex-col">
            {/* Profile Section */}
            <StudentProfile
              students={[...knownNames]}
              activeStudent={activeStudent}
              studentImages={studentImages}
              onStudentSelect={setActiveStudent}
              onImageUpload={handleImageUpload}
            />

            {/* Timeline Section */}
            {activeStudent && features[FEATURES.TIMELINE] && (
              <div className="border-t border-gray-200">
                <Timeline
                  messages={messages}
                  studentName={activeStudent}
                  selectedCategory={activeCategory}
                  onEntryClick={handleTimelineClick}
                />
              </div>
            )}

            {/* Category Filters */}
            {activeStudent && features[FEATURES.CATEGORY_FILTERS] && (
              <CategoryFilters
                messages={messages}
                activeStudent={activeStudent}
                activeCategory={activeCategory}
                setActiveCategory={setActiveCategory}
              />
            )}

            {/* Student Messages Section */}
            {activeStudent && (
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-2">
                  {getFilteredMessages(messages, activeStudent, activeCategory).map((message, index) => (
                    <MessageCard
                      key={message.id}
                      message={message}
                      index={index}
                      isHighlighted={highlightedMessage === message.id}
                      onToggleSentiment={handleToggleSentiment}
                      onUpdateCategory={handleUpdateCategory}
                      onAddNote={handleAddNote}
                      onAddAttachment={handleAddAttachment}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Demo Controls */}
      <DemoControls 
        onSubmitMessage={text => {
          setInputText(text);
          handleSendMessage();
        }}
        setMessages={setMessages}
        setInputText={setInputText}
      />
    </div>
  );
};

export default ObservationChatWindow;